import { useState } from 'react';
import { Check, Copy, MessageSquare, Send } from 'lucide-react';
import { startChatThread } from '../../../Services/APIs/chat';
import { getClaraviewLink } from '../../../Utils/claraviewLink';
import { getClinicDetails } from '../../../Utils/clinicAuth';
import { Banner } from './ModalShell';
import {
    PRIMARY_BUTTON,
    SECONDARY_BUTTON,
    QUIET_BUTTON,
    MICRO_LABEL,
    DEFAULT_PATIENT_MESSAGE,
} from './constants';

const DoneStep = ({ patientName, patientId, patientEmail, exitType, onGoToMessages, onClose }) => {
    const [copied, setCopied] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [chatToken, setChatToken] = useState(null);

    const clinic = getClinicDetails() || {};
    const clinicLink = getClaraviewLink(clinic.company);

    // No patient record or clinic to attach a thread to means the CTA can't work — don't offer it.
    const showSend = exitType === 'share' && Boolean(patientId && clinic._id && patientEmail);

    const copyLink = () => {
        navigator.clipboard.writeText(clinicLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const sendToPatient = async () => {
        setSendError(null);
        setSending(true);
        try {
            const response = await startChatThread({
                customerEmail: patientEmail,
                subject: DEFAULT_PATIENT_MESSAGE.subject,
                message: DEFAULT_PATIENT_MESSAGE.message,
                sentBy: (clinic.doctor_name || 'Doctor').trim(),
                patient: patientId,
                company: clinic._id,
            });
            setChatToken(response?.data?.chatToken || response?.chatToken || null);
            setSent(true);
        } catch (err) {
            setSendError(
                err?.response?.data?.message || "We couldn't send this to the patient. Please try again."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div>
            {sendError && <Banner>{sendError}</Banner>}

            <div className="flex flex-col items-center pt-2 pb-5">
                <div className="w-12 h-12 rounded-full bg-[#EEF3F0] flex items-center justify-center mb-3">
                    <Check className="w-5 h-5 text-[#38433A]" />
                </div>
                <p className="text-[17px] text-[#2D2D2D] text-center mb-1">
                    {patientName.trim()} added to records
                </p>
                <p className="text-[13px] text-[#6B6B6B] text-center break-all">
                    {sent
                        ? `Sent to ${patientEmail}`
                        : exitType === 'share'
                            ? 'Share your Claraview link'
                            : 'Patient record saved'}
                </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
                <input
                    type="text"
                    readOnly
                    value={clinicLink}
                    className="flex-1 min-w-0 px-4 py-3 bg-[#FAFAF9] border border-[#E8E6E3] rounded-lg text-[#2D2D2D] text-sm focus:outline-none"
                />
                <button
                    type="button"
                    onClick={copyLink}
                    className="shrink-0 h-11 px-5 inline-flex items-center justify-center gap-2 bg-[#2D2D2D] text-white rounded-lg hover:bg-[#1A1A1A] transition-colors"
                >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className={MICRO_LABEL}>{copied ? 'Copied' : 'Copy Link'}</span>
                </button>
            </div>

            <div className="flex flex-col gap-2">
                {showSend && (
                    <button
                        type="button"
                        onClick={sendToPatient}
                        disabled={sending || sent}
                        className={PRIMARY_BUTTON}
                    >
                        {sending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        ) : sent ? (
                            <Check className="w-3.5 h-3.5" />
                        ) : (
                            <Send className="w-3.5 h-3.5" />
                        )}
                        <span className={MICRO_LABEL}>
                            {sending ? 'Sending…' : sent ? 'Sent' : 'Send to Patient'}
                        </span>
                    </button>
                )}

                {exitType === 'share' && (
                    <button
                        type="button"
                        onClick={() => onGoToMessages(chatToken)}
                        className={showSend ? SECONDARY_BUTTON : PRIMARY_BUTTON}
                    >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span className={MICRO_LABEL}>Go to Messages</span>
                    </button>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className={showSend ? QUIET_BUTTON : SECONDARY_BUTTON}
                >
                    <span className={MICRO_LABEL}>Done</span>
                </button>
            </div>
        </div>
    );
};

export default DoneStep;
